import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { roadTestApi } from '@/fetch/bussiness/roadtest/api';
import {
  ApiResponse,
  RoadTestTaskDetail,
} from '@/fetch/bussiness/roadtest/types';
import { HttpStatusCode } from '@/fetch/core/constant';

const POLL_INTERVAL_MS = 5000;

const isTaskNotFound = (res?: ApiResponse) => {
  if (!res) return false;
  if (res.code === HttpStatusCode.Success) return false;
  const msg = res.message || '';
  return /不存在|not found|找不到|task.*not/i.test(msg);
};

export interface UseRoadTestTaskDetailOptions {
  /** 是否开启轮询 */
  enabled: boolean;
}

export interface UseRoadTestTaskDetailResult {
  /** 当前任务详情（finished 或尚未请求成功时为 null） */
  taskDetail: RoadTestTaskDetail | null;
  /** 任务是否已结束 */
  finished: boolean;
  /** 是否处于首次加载中 */
  loading: boolean;
}

/**
 * 路测任务详情 HTTP 轮询 Hook
 *
 * - 轮询间隔 POLL_INTERVAL_MS，页面隐藏不暂停
 * - 组件卸载时清理定时器
 * - 接口报错时 toast 提示并继续轮询
 * - 返回 finished=true 或任务不存在时停止轮询并清空 taskDetail
 * - 关闭轮询（enabled=false）时重置 taskDetail 与 finished
 */
export function useRoadTestTaskDetail(
  vehicleName: string,
  options: UseRoadTestTaskDetailOptions
): UseRoadTestTaskDetailResult {
  const { enabled } = options;

  // 任务详情数据
  const [taskDetail, setTaskDetail] = useState<RoadTestTaskDetail | null>(null);
  // 任务是否结束（数据从获取详情接口获取）
  const [finished, setFinished] = useState(false);
  // 是否处于首次加载中（首次请求接口前为 true，接口返回后为 false）
  const [loading, setLoading] = useState(false);

  /**
   * 获取路测任务详情的函数，供轮询使用
   */
  const fetchTaskDetail = useCallback(async () => {
    try {
      const res = await roadTestApi.getRoadTaskDetail({
        vehicleName,
      });

      // 处理错误情况
      if (res?.code !== HttpStatusCode.Success) {
        if (isTaskNotFound(res)) {
          setFinished(true);
          setTaskDetail(null);
          return;
        }
        message.error(res?.message || '获取路测任务详情失败');
        return;
      }

      // 处理成功情况
      const data = res.data;
      if (data?.finished) {
        // 任务结束
        setFinished(true);
        setTaskDetail(null);
      } else {
        // 任务未结束，更新 taskDetail
        setFinished(false);
        setTaskDetail({
          ...data,
          itemList: data?.itemList || [],
          defectList: data?.defectList || [],
          accidentList: data?.accidentList || [],
        } as RoadTestTaskDetail);
      }
    } catch (e) {
      message.error('获取路测任务详情失败，继续轮询');
    }
  }, [vehicleName]);

  /**
   * 初始化轮询
   */
  useEffect(() => {
    // 关闭轮询时清空状态，避免无任务时残留旧任务
    if (!enabled || !vehicleName) {
      setLoading(false);
      setFinished(false);
      setTaskDetail(null);
      return;
    }

    setLoading(true);
    let cancelled = false;

    // 轮询函数
    const poll = async () => {
      if (cancelled) return;
      await fetchTaskDetail();
      // 防止在 fetchTaskDetail 异步执行期间 effect 已经被清理后，仍然去 setLoading(false)
      if (!cancelled) {
        setLoading(false);
      }
    };

    // 开始轮询
    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [vehicleName, enabled, fetchTaskDetail]);

  return { taskDetail, finished, loading };
}
